// The attention feed board: tabs, round bar, "Needs you now", routine groups and the card list.
// Wide screens show the selected card beside the list (nothing moves while you read it); narrow
// screens open a card on its own page. Adapted in spirit from EveryInc/tend src/App.tsx (MIT,
// Copyright (c) 2026 Every, Inc.), decomposed into routes and hooks.
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { getRouteApi, Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { CardSummary, CardView } from "@shared/api";
import { useAccountLookup } from "@web/lib/accounts";
import { cn } from "@web/lib/cn";
import { isApiError } from "@web/lib/api";
import { feedApi } from "@web/lib/feedApi";
import { feedKeys, prefetchCard } from "@web/lib/feedQueries";
import { markCardSwitch, useCardPainted } from "@web/measure/useMeasure";
import { useVerdictCommand } from "@web/measure/verdictStore";
import { cardTarget } from "@web/measure/verdicts";
import { RAIL_QUERY, useMediaQuery, WIDE_QUERY } from "@web/lib/useMediaQuery";
import { useNow } from "@web/lib/useNow";
import { useCommand } from "@web/shell/commands";
import { dock } from "@web/shell/dockStore";
import { Button } from "@web/ui/Button";
import { DayAtAGlance } from "@web/day/DayAtAGlance";
import { SwipeableCardRow } from "@web/notify/SwipeableCardRow";
import { FeedPickUp } from "@web/focus/PickUp";
import { ActiveCard } from "./ActiveCard";
import { Calm } from "./Calm";
import { BoardTabs, LearningOffer, NeedsYouNow, PassBar, rowId, useNextPass } from "./BoardParts";
import { RoutineGroupBlock, RoutineGroupRow } from "./RoutineGroupBlock";
import { NextRoundOffer } from "./RoundStart";
import { SnoozedTray } from "./SnoozedTray";
import {
  groupIdOfNavId,
  passCount,
  routineNavId,
  shouldVirtualize,
  TAB_LABEL,
  type BoardTab,
} from "./selectors";
import { ThreadDrawer } from "./ThreadDrawer";
import { useBoardModel, useSelectedCard } from "./useBoardModel";
import { VirtualCardList } from "./VirtualCardList";

const route = getRouteApi("/_app/feed");

const EMPTY: Record<BoardTab, { title: string; body: string }> = {
  review: {
    title: "End of this round",
    body: "Nothing is waiting for you in this round. New mail waits for the next one.",
  },
  queued: {
    title: "Nothing lined up",
    body: "What you hand to Claude or approve waits here until it starts.",
  },
  working: { title: "Nothing in progress", body: "Claude isn't working on anything right now." },
  done: { title: "Nothing done yet", body: "Decided cards stay here for a while." },
};

function SkeletonRows() {
  return (
    <div aria-busy className="flex flex-col">
      {Array.from({ length: 5 }, (_, i) => (
        <div
          key={i}
          aria-hidden
          className="flex flex-col gap-2 border-b border-line bg-paper px-5 py-4"
        >
          <div className="h-2.5 w-24 rounded bg-sunk" />
          <div className="h-4 w-3/4 rounded bg-sunk" />
          <div className="h-3 w-5/6 rounded bg-sunk" />
        </div>
      ))}
    </div>
  );
}

export function FeedPage() {
  const search = route.useSearch();
  const navigate = route.useNavigate();
  const queryClient = useQueryClient();
  const tab: BoardTab = search.tab ?? "review";
  const model = useBoardModel();
  const { board, sections, feedId } = model;
  const wide = useMediaQuery(WIDE_QUERY);
  // Wide desktops get "Your day" as a third column; narrower screens find it on Tasks.
  const rail = useMediaQuery(RAIL_QUERY);
  const now = useNow(60_000);
  const { list: accounts, colorIndex, label } = useAccountLookup();
  const [drawer, setDrawer] = useState(false);
  const next = useNextPass(feedId);
  const listRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const cards = useMemo(() => (sections ? sections[tab] : []), [sections, tab]);
  const routine = useMemo(() => (sections ? sections.routine[tab] : []), [sections, tab]);
  // j/k walk the cleanups above the cards, then the cards; selection starts at the first card.
  const navIds = useMemo(
    () => [...routine.map((g) => routineNavId(g.id)), ...cards.map((c) => c.id)],
    [routine, cards],
  );
  const sel = useSelectedCard(feedId, tab, navIds, cards.length ? routine.length : 0);
  const selectedGroupId = groupIdOfNavId(sel.selectedId);
  const selectedGroup = selectedGroupId
    ? (routine.find((g) => g.id === selectedGroupId) ?? null)
    : null;
  const selected = selectedGroup ? null : (cards.find((c) => c.id === sel.selectedId) ?? null);
  const cardIndex = Math.max(-1, sel.index - routine.length);
  const ready = board?.pass.readyNext.count ?? 0;
  const virtual = shouldVirtualize(tab, cards.length);

  useEffect(() => dock.prefer("card"), []);

  // Warm the next few cards so j feels instant.
  useEffect(() => {
    for (const card of cards.slice(cardIndex + 1, cardIndex + 4))
      prefetchCard(queryClient, card.id);
  }, [cards, cardIndex, queryClient]);

  // Keep the selected row in view when j/k move it (not on arrival: the page starts at the top).
  const lastSelected = useRef(sel.selectedId);
  useEffect(() => {
    if (!sel.selectedId || lastSelected.current === sel.selectedId) return;
    const first = lastSelected.current === null;
    lastSelected.current = sel.selectedId;
    if (first) return;
    document.getElementById(rowId(sel.selectedId))?.scrollIntoView({ block: "nearest" });
  }, [sel.selectedId]);

  useEffect(() => {
    if (document.activeElement === document.body) listRef.current?.focus({ preventScroll: true });
  }, []);

  const openCard = (card: CardSummary) => {
    sel.select(card.id);
    void navigate({ to: "/feed/cards/$cardId", params: { cardId: card.id } });
  };
  const onRow = (card: CardSummary) => (wide ? sel.select(card.id) : openCard(card));
  const setTab = (t: BoardTab) =>
    void navigate({ search: { tab: t === "review" ? undefined : t }, replace: true });

  const recollect = async () => {
    if (!feedId) return;
    try {
      await feedApi.recollect(feedId);
      toast("Claude is looking again. New cards wait for the next round.");
    } catch (error) {
      toast(
        isApiError(error, "not_available")
          ? "Claude isn't wired up yet."
          : "Couldn't start that. Try again.",
      );
    }
  };

  // j/k time the switch until the next card paints (local measure, U0).
  const move = (delta: 1 | -1) => {
    markCardSwitch(sel.selectedId);
    void sel.move(delta);
  };
  useCardPainted(sel.selectedId);
  useCommand("card.next", () => move(1), navIds.length > 0);
  useCommand("card.prev", () => move(-1), navIds.length > 0);
  // Narrow screens list cards without opening them: `!` judges the selected row.
  useVerdictCommand(() => {
    if (!selected) return null;
    const full = queryClient.getQueryData<CardView>(feedKeys.card(selected.id));
    return cardTarget(full ?? selected, "card");
  }, !wide && !!selected);
  useCommand("card.open", () => (selected ? openCard(selected) : undefined), !!selected);
  useCommand("card.thread", () => setDrawer((d) => !d), !!selected);
  useCommand("card.cancel", () => setDrawer(false), drawer);
  useCommand("feed.focus", () => void navigate({ to: "/focus" }));
  useCommand("feed.nextPass", () => {
    if (ready > 0) void next.start();
    else toast("Nothing new is waiting for the next round yet.");
  });
  useCommand("feed.recollect", () => void recollect(), !!feedId);

  const onBreakthrough = (card: CardSummary) => {
    if (tab === "review" && cards.some((c) => c.id === card.id)) onRow(card);
    else openCard(card);
  };

  let list: ReactNode;
  if (model.noFeed) {
    list = (
      <Calm
        title="Your feed is getting ready"
        body="Once an account is connected and Claude has read your mail, cards appear here."
      />
    );
  } else if (model.isPending) {
    list = <SkeletonRows />;
  } else if (model.isError && !board) {
    list = (
      <Calm title="Couldn’t load the feed" body="The server didn’t answer. Nothing is lost.">
        <Button onClick={model.refetch}>Try again</Button>
      </Calm>
    );
  } else if (!cards.length && !routine.length) {
    list = (
      <Calm {...EMPTY[tab]}>
        {tab === "review" && ready > 0 && board ? (
          <NextRoundOffer
            estimate={board.pass.nextEstimate}
            busy={next.busy}
            onStart={(order) => void next.start(order)}
            size="md"
          />
        ) : tab === "review" ? (
          <Link to="/fresh-start" className="text-sm text-muted underline underline-offset-2">
            Older mail in the way? Fresh start sets it aside.
          </Link>
        ) : null}
      </Calm>
    );
  } else {
    list = (
      <>
        {routine.length && !wide ? (
          <div className="flex flex-col gap-3 px-4 py-3 md:px-5">
            {routine.map((g) => {
              const navId = routineNavId(g.id);
              const on = navId === sel.selectedId;
              return (
                <RoutineGroupBlock
                  key={g.id}
                  id={rowId(navId)}
                  group={g}
                  active={on}
                  selected={on}
                  onSelect={() => sel.select(navId)}
                />
              );
            })}
          </div>
        ) : null}
        <div
          ref={listRef}
          role="listbox"
          aria-label={`Cards: ${TAB_LABEL[tab]}`}
          tabIndex={0}
          aria-activedescendant={sel.selectedId ? rowId(sel.selectedId) : undefined}
          className="border-t border-line outline-none"
        >
          {wide
            ? routine.map((g) => {
                const navId = routineNavId(g.id);
                return (
                  <RoutineGroupRow
                    key={g.id}
                    id={rowId(navId)}
                    group={g}
                    selected={navId === sel.selectedId}
                    onSelect={() => sel.select(navId)}
                  />
                );
              })
            : null}
          {virtual ? (
            <VirtualCardList
              cards={cards}
              scrollRef={scrollRef}
              selectedId={sel.selectedId}
              showAccount={accounts.length > 1}
              colorIndex={colorIndex}
              accountLabel={label}
              now={now}
              onSelect={onRow}
            />
          ) : (
            cards.map((card) => (
              <SwipeableCardRow
                key={card.id}
                card={card}
                selected={card.id === sel.selectedId}
                showAccount={accounts.length > 1}
                colorIndex={card.accountId ? colorIndex(card.accountId) : 0}
                accountLabel={card.accountEmail ? label(card.accountEmail) : undefined}
                now={now}
                onSelect={onRow}
              />
            ))
          )}
        </div>
        {tab === "review" ? (
          <div className="flex flex-col items-center gap-2 px-4 py-6 text-center">
            <p className="mm-kicker">End of this round</p>
            {ready > 0 && board ? (
              <NextRoundOffer
                estimate={board.pass.nextEstimate}
                busy={next.busy}
                onStart={(order) => void next.start(order)}
                emphasis="secondary"
              />
            ) : (
              <p className="text-sm text-muted">New mail waits for the next round.</p>
            )}
          </div>
        ) : null}
      </>
    );
  }

  // Below the end of the list: what is snoozed and when it comes back (folded, no count).
  const tray = board && feedId && tab === "review" ? <SnoozedTray feedId={feedId} /> : null;

  const passBar =
    board && sections && tab === "review" ? (
      <PassBar
        board={board}
        count={passCount(sections)}
        roundCards={sections.review}
        progress={model.progress}
      />
    ) : null;

  const notices =
    board && sections ? (
      <>
        {tab === "review" ? (
          <NeedsYouNow cards={sections.breakthrough} onOpen={onBreakthrough} />
        ) : null}
        {tab === "review" ? (
          <FeedPickUp
            feedId={feedId}
            currentPass={board.pass.current}
            hasOpenCards={sections.review.length > 0}
          />
        ) : null}
        {tab === "review" && board.learningOffer && feedId ? (
          <LearningOffer feedId={feedId} />
        ) : null}
        {board.sweep.recollectionOffered ? (
          <div className="mx-4 mt-3 flex flex-wrap items-center gap-2 text-sm text-muted md:mx-5">
            <span>Claude may have missed something.</span>
            <button
              type="button"
              className="text-accent-text underline"
              onClick={() => void recollect()}
            >
              Look again
            </button>
          </div>
        ) : null}
        <div className="h-3" />
      </>
    ) : null;

  return (
    <div className="flex h-full flex-col">
      <BoardTabs tab={tab} onTab={setTab} sections={sections} budget={board?.budget} />
      {wide ? (
        <>
          {passBar ? <div className="shrink-0 pb-3">{passBar}</div> : null}
          <div
            className={cn(
              "grid min-h-0 flex-1 border-t border-line",
              rail
                ? "grid-cols-[minmax(300px,380px)_minmax(0,1fr)_minmax(250px,290px)]"
                : "grid-cols-[minmax(300px,380px)_minmax(0,1fr)]",
            )}
          >
            <div
              ref={scrollRef}
              className="min-h-0 overflow-y-auto border-r border-line bg-paper/40"
            >
              {notices}
              {list}
              {tray}
            </div>
            <div className="min-h-0 overflow-y-auto">
              {selectedGroup ? (
                // Room below so a confirmation never sits under the Dock.
                <div className="mx-auto max-w-3xl px-6 pt-5 pb-16">
                  <RoutineGroupBlock
                    key={selectedGroup.id}
                    group={selectedGroup}
                    active
                    defaultOpen
                  />
                </div>
              ) : selected && board ? (
                <div className="mx-auto max-w-3xl px-6 pt-5 pb-16">
                  <ActiveCard
                    key={selected.id}
                    summary={selected}
                    feed={board.feed}
                    active
                    onToggleThread={() => setDrawer((d) => !d)}
                  />
                </div>
              ) : (
                <Calm
                  title={navIds.length ? "Pick a card" : "Nothing to show here"}
                  body={
                    navIds.length
                      ? "Use j and k, or click a card on the left."
                      : "Other tabs may have something."
                  }
                />
              )}
            </div>
            {rail ? (
              <aside className="min-h-0 overflow-y-auto border-l border-line bg-paper/40">
                <DayAtAGlance variant="rail" />
              </aside>
            ) : null}
          </div>
        </>
      ) : (
        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
          {passBar}
          {notices}
          {list}
          {tray}
        </div>
      )}
      {drawer && selected ? (
        <ThreadDrawer
          key={selected.id}
          cardId={selected.id}
          threadKey={selected.threadKey}
          title={selected.title}
          onClose={() => setDrawer(false)}
        />
      ) : null}
    </div>
  );
}
